import { useState, useRef, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { useIntl } from "react-intl";
import { NotificationContainer } from "Components/Notifications";

//Components
import { Tooltip, Button, Form, Input, ToggleSwitch, Select, RadioButton } from "@gpstrackit/react-controls";
import CustomCard from 'Components/Card';
import { Alert, AlertTitle, Icon, Chip, Grid } from '@mui/material';
import { Loading } from "@gpstrackit/react-controls";
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

//Icons
import Success from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';

//Notification
import { NotificationManager } from "Components/Notifications";
import _ from 'lodash';

//Helpers
import { copyToClipboard } from 'Core/data/Helpers';

//Actions
import {
    sendCommandGeneralRequest,
    sendCommandGetDeviceRequest,
    sendCommandCloseModal,
    sendCommandSearchDeviceModelRequest,
    sendCommandSearchCommandsRequest,
    sendCommandSearchMassCommandsRequest,
    sendCommandSetDevices,
    sendCommandRequest,
    searchEntityRequest,
    sendCommandGetDetailsRequest,
    clearRedux,
} from 'Redux/actions';

//Styles
import './index.scss';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const PROJECT_ID_PM = 'PM';

const EMPTY_MASS_COMMAND_GROUPS = {
    manufacturer: null,
    unitTypes: [],
    deviceModels: [],
};

const normalizeToArray = (value) => {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
};

const SendCommand = (props) => {
    const { single, loadData, setFromOverview, setFromTab } = props;
    const formRef = useRef();
    const commandRef = useRef();
    const dispatch = useDispatch();
    const { messages } = useIntl();
    const navigate = useNavigate();

    //Redux States
    const { loading, openModal, result, device, devices, deviceModels, commands, massCommandGroups, detailsResult, massSMS } = useSelector(state => state.sendCommandRedux);
    const unitTypes = useSelector(state => state.generalRedux.unitTypes);
    const manufacturers = useSelector(state => state.generalRedux.manufacturers);
    const smsIntegrations = useSelector(state => state.generalRedux.smsIntegrations);
    const generalDeviceModels = useSelector(state => state.generalRedux.deviceModels);
    const loadingEntity = useSelector(state => state.generalRedux.loadingEntity);

    //Local States
    const [serial, setSerial] = useState("");
    const [step, setStep] = useState(1);
    const [sendCommandBy, setSendCommandBy] = useState("SMS");
    const [openCommand, setOpenCommand] = useState(false);
    const [rawCommand, setRawCommand] = useState("");
    const [cannedCommand, setCannedCommand] = useState(null);
    const [sentMessages, setSentMessages] = useState([]);
    const [unitType, setUnitType] = useState(null);
    const [sendType, setSendType] = useState(null);
    const [phoneNumbers, setPhoneNumbers] = useState("");
    const [phoneNumbersErrors, setPhoneNumbersErrors] = useState([]);
    const [phones, setPhones] = useState([]);
    const [unitTypeId, setUnitTypeId] = useState(-1);
    const [manufacturerId, setManufacturerId] = useState(-1);
    const [type, setType] = useState(null);
    const [expandedAll, setExpandedAll] = useState(false)
    const [smsDetails, setSmsDetails] = useState([])
    const [manufacturer, setManufacturer] = useState(null)
    const [massSmsUnitTypes, setMassSmsUnitTypes] = useState([])
    const [massSmsDeviceModels, setMassSmsDeviceModels] = useState([])
    const [massCommandSelections, setMassCommandSelections] = useState({})

    // M2MD Waldo locate options (HTTP)
    const [locateAccuracy, setLocateAccuracy] = useState(5)
    const [locateSkipWifi, setLocateSkipWifi] = useState(false)
    const [locateSkipGps, setLocateSkipGps] = useState(false)

    //Local Variables
    const phoneNumberTypes = ["Jasper", "AT&T smpp", "Intouch AT&T smpp", "Fleettrax AT&T smpp"]
    const normalizedMassSmsUnitTypes = normalizeToArray(massSmsUnitTypes);
    const normalizedMassSmsDeviceModels = normalizeToArray(massSmsDeviceModels);
    const availableDeviceModels = (generalDeviceModels || []).filter((item) =>
        normalizedMassSmsUnitTypes.some((unitTypeItem) => unitTypeItem?.id === item?.unitTypeId)
    );

    const closeModal = () => {
        dispatch(sendCommandCloseModal());
        setSerial("");
        setStep(1);
        setSendCommandBy("SMS");
        setOpenCommand(false);
        setRawCommand("");
        setCannedCommand(null);
        setUnitType(null)
        dispatch(clearRedux("SEND_COMMAND"));
        setPhoneNumbers("");
        setPhoneNumbersErrors([]);
        setPhones([]);
        setUnitTypeId(-1);
        setManufacturerId(-1);
        setType(null);
        setSmsDetails([]);
        setExpandedAll(false)
        setManufacturer(null)
        setMassSmsUnitTypes([])
        setMassSmsDeviceModels([])
        setMassCommandSelections({})

        if (props.setMassSMS)
            props.setMassSMS(false)

        loadData && loadData();
        setFromOverview && setFromOverview(false);
        setFromTab && setFromTab(false);

    }

    const searchDevice = () => {
        dispatch(sendCommandGetDeviceRequest(serial?.trim()))
    }

    const stepHandler = () => {
        setStep(step + 1)
        if (step + 1 === 2) {
            searchCannedCommand(devices[0]?.deviceModel?.unitTypeId, devices[0]?.deviceModel?.manufacturerId)
        }
    }

    const searchCannedCommand = (unitTypeId, manufacturerId, sendVia) => {
        let data = {}
        setUnitTypeId(unitTypeId);
        setManufacturerId(manufacturerId);
        data.unitTypeId = unitTypeId;
        data.manufacturerId = manufacturerId;
        data.via = !sendVia ? sendCommandBy : sendVia;
        dispatch(sendCommandSearchCommandsRequest(data));
    }

    const changeSendCommandVia = (via) => {
        setSendCommandBy(via);
        setCannedCommand(null);
        setOpenCommand(false);
        setRawCommand("");
        searchCannedCommand(unitTypeId, manufacturerId, via);
    }

    const isM2mdManufacturer = useMemo(() => {
        return `${devices?.[0]?.deviceModel?.manufacturer?.name || ""}`.toUpperCase() === "M2MD";
    }, [devices]);

    const isLocateCannedCommandSelected = useMemo(() => {
        const label = `${cannedCommand?.label || ""}`.trim().toUpperCase();
        const command = `${cannedCommand?.command || ""}`.trim().toUpperCase();
        const isLocateLabel = label === "LOCATE";
        const isM2mdLocateCommand = command.includes("M2MD_WALDO_LOCATE");
        const isExactLocateCommand = command === "LOCATE";
        const isPrefixedLocateCommand = command.startsWith("LOCATE ");
        return (
            isLocateLabel ||
            isM2mdLocateCommand ||
            isExactLocateCommand ||
            isPrefixedLocateCommand
        );
    }, [cannedCommand]);

    const applyChanges = () => {
        let payloadCommands = {
            command: "",
            sms: "",
            to: []
        }
        let to = []
        let parsedPhones = [];
        if (!massSMS) {
            if (sendCommandBy === 'HTTP' && !isM2mdManufacturer) {
                NotificationManager.warning(
                    messages['send_commands_only_for_m2md'],
                    messages['send_commands_title'],
                    3000
                );
                return;
            }

            if (sendCommandBy === 'HTTP' && (!cannedCommand || !isLocateCannedCommandSelected)) {
                NotificationManager.warning(
                    messages['send_commands_http_validate_selection'],
                    messages['send_commands_title'],
                    3000
                );
                return;
            }

            const entityNameMap = {
                SMS: "SmsCommand",
                OTA: "OtaCommand",
                HTTP: "HttpCommand"
            };
            const entityName = entityNameMap[sendCommandBy] || "HttpCommand";
            to = [{ entityName, entityIds: [] }];
            if (devices && devices.length) {
                devices.map((device) => {
                    to[0].entityIds.push(device?.id)
                })
                payloadCommands.to = to;
            }
        } else {
            to = [{ entityName: "Sms", recipients: [] }];

            let msisdns = phoneNumbers.split(/[\s,;\t\n]+/); //split by space, line break, tab, comma, semicolon
            let errors = [];
            msisdns.map((msisdn) => {
                let phone = msisdn.trim()
                if (phone !== "") {
                    if (!isNaN(phone)) {
                        if (phoneNumberTypes.includes(type)) {
                            if (phone.length > 9 && phone.length < 21) {
                                if (parsedPhones.some(x => x === phone)) {
                                    errors.push({ phoneNumber: phone, error: "send_commands_error_duplicate_iccid" })
                                } else {
                                    to[0].recipients.push({ to: phone, smsIntegrationId: +sendType })
                                    parsedPhones.push(phone);
                                }
                            } else {
                                errors.push({ phoneNumber: phone, error: "send_commands_error_iccid_length" })
                            }
                        } else {
                            if (phone.length > 9 && phone.length < 16) {
                                if (parsedPhones.some(x => x === phone)) {
                                    errors.push({ phoneNumber: phone, error: "send_commands_error_duplicate_phone" })
                                } else {
                                    to[0].recipients.push({ to: phone, smsIntegrationId: +sendType })
                                    parsedPhones.push(phone);
                                }
                            } else {
                                errors.push({ phoneNumber: phone, error: "send_commands_error_phone_length" })
                            }
                        }

                    } else {
                        errors.push({ phoneNumber: phone, error: "send_commands_error_phone_not_number" })
                    }
                }
            })
            payloadCommands.to = to;
            setPhoneNumbersErrors(errors)
            setPhones(parsedPhones)
        }
        if (!massSMS ? to.length === 0 : parsedPhones.length === 0) {
            NotificationManager.warning(
                phoneNumberTypes.includes(type) ? messages['send_commands_unvalid_iccid_numbers'] : messages['send_commands_unvalid_phone_numbers'],
                messages['send_commands_title'],
                3000
            );
        } else {
            if (!massSMS) {

                if (sendCommandBy === 'HTTP') {
                    payloadCommands.command = JSON.stringify({
                        action: 'M2MD_WALDO_LOCATE',
                        accuracy:
                            (locateAccuracy === '' ||
                                locateAccuracy === null ||
                                typeof locateAccuracy === 'undefined')
                                ? undefined
                                : (Number.isNaN(Number(locateAccuracy)) ? undefined : Number(locateAccuracy)),
                        skipWifi: !!locateSkipWifi,
                        skipGps: !!locateSkipGps,
                    })

                    payloadCommands.projectId = PROJECT_ID_PM
                }
                else {
                    if (!openCommand) {
                        payloadCommands.command = cannedCommand.command
                    } else {
                        payloadCommands.command = rawCommand
                    }
                }
                if (payloadCommands.sms === "") {
                    delete payloadCommands.sms
                }
            } else {
                payloadCommands.sms = rawCommand
                delete payloadCommands.command
            }
            if (massSMS) {
                dispatch(sendCommandRequest(payloadCommands));
            } else {
                dispatch(sendCommandGeneralRequest(payloadCommands))
            }
        }
    }

    //to expande or collapse all
    const collapseAll = (expand) => {
        let details = smsDetails.map((item) => { return { ...item, expanded: expand } });
        setSmsDetails(details);
        setExpandedAll(expand);
    }

    const clearMassCommandState = () => {
        setMassCommandSelections({});
        setRawCommand("");
        dispatch(clearRedux("SEND_COMMAND", { massCommandGroups: EMPTY_MASS_COMMAND_GROUPS }));
    }

    const updateMassCommandSelection = (key, value) => {
        setMassCommandSelections((currentSelections) => ({
            ...currentSelections,
            [key]: value || null,
        }));

        if (value?.command) {
            setRawCommand(value.command);
        }
    }

    useEffect(() => {

        if (device) {
            if (!device?.total) {
                NotificationManager.warning(
                    messages['send_commands_the_device_has_not_been_found'],
                    messages['send_commands_title'],
                    3000
                );
            } else {
                const { items } = device;
                if (devices.length > 0) {
                    let currentDevices = devices;
                    let currentUnitId = items[0]?.id
                    let currentManufacturer = devices[0]?.deviceModel?.manufacturer?.name.toUpperCase();
                    let currentUnitTypeId = devices[0]?.deviceModel?.unitTypeId;
                    let manufacturersValidation = items.some((item) => item?.deviceModel?.manufacturer?.name?.toUpperCase() !== currentManufacturer);
                    let unitTypeIdValidation = items.some((item) => item?.deviceModel?.unitTypeId !== currentUnitTypeId);
                    let unitIdValidation = currentDevices.some((item) => item?.id === currentUnitId);
                    if (unitIdValidation) {
                        NotificationManager.warning(
                            messages['send_commands_the_device_has_already_been_added'],
                            messages['send_commands_title'],
                            3000
                        );
                        setSerial("");
                        dispatch(clearRedux("SEND_COMMAND", { device: null }))
                        return
                    }
                    if (!manufacturersValidation) {
                        if (currentManufacturer === "GEOMETRIS" || !unitTypeIdValidation) {
                            currentDevices.push(...items)
                            dispatch(sendCommandSetDevices(currentDevices));
                            setSerial("")
                        } else {
                            NotificationManager.warning(
                                messages['send_commands_the_unit_type_of_the_device_must_bet_the_same'],
                                messages['send_commands_title'],
                                3000
                            );
                            setSerial("");
                        }
                    } else {
                        NotificationManager.warning(
                            messages['send_commands_the_manufacturer_of_the_devices_must_be_the_same'],
                            messages['send_commands_title'],
                            3000
                        );
                    }
                } else {
                    dispatch(sendCommandSetDevices(items));
                }
            }
            dispatch(clearRedux("SEND_COMMAND", { device: null }))
        }
        if (result) {

            let transactionId = result?.transactionId;
            if (transactionId) {
                let message = messages['mass_sms_request_has_been_received']
                NotificationManager.success(message, messages['send_commands_title'], 10000, () => {
                    copyToClipboard(transactionId, messages['common_copied']);
                    navigate(`/app/transactions/list/${transactionId}`);
                }, null, '', null);
            }

            let currentMessage = result[0]?.description;
            if (currentMessage) {
                let currentSentMessages = sentMessages;
                currentSentMessages.push(currentMessage)
                setSentMessages(currentSentMessages);
            }
            setStep(3);
            //setRawCommand("");
            dispatch(clearRedux("SEND_COMMAND", { result: null }))
            //phoneNumberTypes.includes(type)
            if (massSMS) {
                dispatch(sendCommandGetDetailsRequest({ type: phoneNumberTypes.includes(type) ? 'ICCID' : 'MSISDN', entities: phones }))
            }
        }
        if (deviceModels?.items) {
            let { items, total } = deviceModels;
            let currentDevices = JSON.parse(JSON.stringify(devices));
            if (total) {
                items.map((item) => {
                    let indexDevice = currentDevices.findIndex((de) => de?.id === item?.id);
                    if (indexDevice > -1) {
                        currentDevices[indexDevice].deviceModel = item?.deviceModel
                    }
                })
            }
            if (single) {
                searchCannedCommand(currentDevices[0]?.deviceModel?.unitTypeId, currentDevices[0]?.deviceModel?.manufacturerId)
            }
            dispatch(sendCommandSetDevices(currentDevices));
            dispatch(clearRedux("SEND_COMMAND", { deviceModels: null }));
        }
        if (detailsResult && Array.isArray(detailsResult)) {
            let details = detailsResult.map((item) => { return { ...item, expanded: false } });
            setSmsDetails(details);
            dispatch(clearRedux("SEND_COMMAND", { detailsResult: null }));
        }
    }, [device, deviceModels, result, detailsResult]);

    useEffect(() => {
        if (openModal && devices.length > 0) {
            let unitIds = devices.map((item) => (item?.id))
            dispatch(sendCommandSearchDeviceModelRequest(unitIds));
        }
    }, [openModal])

    let validateStepOne = step === 1 && !single && !massSMS
    let validateStepTwo = step === 2 || single || massSMS

    let currentUnitTypeId = devices[0]?.deviceModel.unitTypeId
    let unitTypeIdValidation = devices[0]?.deviceModel?.manufacturer?.name.toUpperCase() === "GEOMETRIS" ? false : devices ? devices.some((item) => item.deviceModel.unitTypeId !== currentUnitTypeId) : false

    useEffect(() => {
        if (sendCommandBy === 'HTTP' && !isM2mdManufacturer) {
            setSendCommandBy('SMS');
            setCannedCommand(null);
            setOpenCommand(false);
            setRawCommand("");

            if (unitTypeId !== -1 && manufacturerId !== -1) {
                searchCannedCommand(unitTypeId, manufacturerId, 'SMS');
            }
        }
        if (sendCommandBy === 'HTTP' && openCommand) {
            setOpenCommand(false);
            setRawCommand("");
        }
    }, [sendCommandBy, isM2mdManufacturer, unitTypeId, manufacturerId, openCommand]);

    useEffect(() => {
        (!unitTypes || unitTypes?.length === 0) && dispatch(searchEntityRequest({ key: "unitTypes" }));
        (!manufacturers || manufacturers?.length === 0) && dispatch(searchEntityRequest({ key: "manufacturers" }));
        dispatch(searchEntityRequest({ key: "smsIntegrations" }));
        (!generalDeviceModels || generalDeviceModels?.length === 0) && dispatch(searchEntityRequest({ key: "deviceModels" }));

        return () => {
            closeModal()
        }
    }, []);

    useEffect(() => {
        if (!massSMS || !manufacturer?.id) return;

        dispatch(sendCommandSearchMassCommandsRequest({
            manufacturerId: manufacturer.id,
            unitTypeIds: normalizedMassSmsUnitTypes.map((item) => item?.id),
            deviceModelIds: normalizedMassSmsDeviceModels.map((item) => item?.id),
        }));
    }, [massSMS, manufacturer, massSmsUnitTypes, massSmsDeviceModels]);

    return (
        <BootstrapDialog
            open={openModal}
            title={!massSMS ? messages['send_commands_title'] : messages['send_commands_mass_sms_title']}
            loading={loading}
            position="right"
            maxWidth={"md"}
            fullWidth={true}
            style={{ height: '100%' }}

        >
            {
                loading &&
                <Loading overlay show />
            }
            <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
                {!massSMS ? messages['send_commands_title'] : messages['send_commands_mass_sms_title']}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={closeModal}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent dividers>
                {
                    validateStepOne &&
                    <div style={{ padding: '0 10px', marginBottom: 30 }}>
                        <Grid
                            container
                            spacing={1}
                        >
                            <Grid item xs={10}>
                                <Form
                                    ref={formRef}
                                    onValidSubmit={() => searchDevice()}
                                >
                                    <Input
                                        label={messages['send_commands_imei_or_serial']}
                                        placeholder={messages['send_commands_imei_or_serial']}
                                        required
                                        requiredMessage={messages['required_field']}
                                        value={serial}
                                        onChange={(value) => setSerial(value)}
                                        maxLength={20}
                                    />
                                </Form>
                            </Grid>
                            <Grid item xs={2}>
                                <div style={{ marginTop: 26 }}>
                                    <Button
                                        className="btn-dark-blue"
                                        onClick={() => formRef?.current?.submit()}
                                    >
                                        {messages['common_add']}
                                    </Button>
                                </div>
                            </Grid>
                        </Grid>
                        {devices?.length > 0 ? devices.map((item, index) => {
                            return (
                                <CustomCard
                                    key={index}
                                    onClick={() => {
                                        let currentDevices = devices;
                                        let selected = currentDevices.findIndex((device) => device.id === item.id);
                                        if (selected > -1) currentDevices.splice(selected, 1);
                                        dispatch(sendCommandSetDevices(currentDevices));
                                    }}

                                >
                                    <div style={{ display: 'grid' }}>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_imei']}</label><span>{item?.imei}</span>
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_serial']}</label><span>{item?.esn}</span>
                                        </div>
                                        <div>
                                            <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_model']}</label><span>{item?.deviceModel?.name}</span>
                                        </div>
                                    </div>
                                </CustomCard>
                            )
                        }) : <div style={{ display: 'flex', justifyContent: 'center', marginTop: 25 }} ><span style={{ opacity: 0.6 }} >{messages['common_no_devices_added']}</span></div>}
                        {
                            unitTypeIdValidation &&
                            <Alert severity="warning" style={{ marginTop: 5 }}>{messages['send_commands_the_unit_type_of_the_device_must_bet_the_same']}</Alert>
                        }
                    </div>
                }
                {
                    validateStepTwo && step !== 3 &&
                    <div style={{ padding: '0 10px' }}>
                        <Grid container>
                            {
                                !massSMS &&
                                <Grid item xs={12}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <label className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1" style={{ marginRight: 5 }}>{messages['devices_bulk_send_command_via']}</label>
                                        <RadioButton
                                            onChange={() => {
                                                changeSendCommandVia('SMS');
                                            }}
                                            value={sendCommandBy === 'SMS'}
                                            label={messages['devices_bulk_actions_sms']}
                                            position="end"
                                        />
                                        <RadioButton
                                            onChange={() => {
                                                changeSendCommandVia('OTA');
                                            }}
                                            value={sendCommandBy === 'OTA'}
                                            label={messages['devices_bulk_actions_ota']}
                                            position="end"
                                        />

                                        {
                                            isM2mdManufacturer &&
                                            <RadioButton
                                                onChange={() => {
                                                    changeSendCommandVia('HTTP');
                                                }}
                                                value={sendCommandBy === 'HTTP'}
                                                label={'HTTP'}
                                                position="end"
                                            />
                                        }
                                    </div>
                                </Grid>
                            }
                            {
                                massSMS &&
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 10 }}>
                                        <Select
                                            id="sendTypes"
                                            label={messages['send_commands_send_type']}
                                            value={smsIntegrations?.length > 0 && smsIntegrations?.find(x => x.id == sendType)}
                                            onChange={(sendType) => {
                                                setSendType(sendType ? sendType?.id : null);
                                                setType(sendType?.name)
                                            }}
                                            options={smsIntegrations || []}
                                            textField="name"
                                            valueField="id"
                                            menuPlacement="auto"
                                            loading={loadingEntity}
                                            placeholder={messages['send_commands_send_type']}
                                            required
                                            requiredMessage={messages['required_field']}
                                        />
                                    </div>
                                </Grid>
                            }

                            {
                                massSMS &&
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 10 }}>
                                        <Select
                                            id="manufacturers"
                                            label={messages['send_commands_manufacturer']}
                                            value={manufacturer}
                                            onChange={(value) => {
                                                setManufacturer(value || null);
                                                setMassSmsUnitTypes([]);
                                                setMassSmsDeviceModels([]);
                                                clearMassCommandState();
                                            }}
                                            options={manufacturers || []}
                                            textField="name"
                                            valueField="id"
                                            menuPlacement="auto"
                                            loading={loadingEntity}
                                            placeholder={messages['send_commands_manufacturer']}
                                        />
                                    </div>
                                </Grid>
                            }
                            {
                                massSMS && manufacturer &&
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 10 }}>
                                        <Select
                                            id="unitTypes"
                                            label={messages['send_commands_unit_types']}
                                            value={normalizedMassSmsUnitTypes}
                                            onChange={(value) => {
                                                setMassSmsUnitTypes(normalizeToArray(value));
                                                setMassSmsDeviceModels([]);
                                                clearMassCommandState();
                                            }}
                                            options={unitTypes.filter(x => x.manufacturerId === manufacturer?.id) || []}
                                            textField="name"
                                            valueField="id"
                                            menuPlacement="auto"
                                            loading={loadingEntity}
                                            placeholder={messages['send_commands_unit_types']}
                                            isMulti
                                        />
                                    </div>
                                </Grid>
                            }
                            {
                                massSMS && manufacturer && normalizedMassSmsUnitTypes.length > 0 &&
                                <Grid item xs={12}>
                                    <div style={{ marginTop: 10 }}>
                                        <Select
                                            id="deviceModels"
                                            label={messages['send_commands_device_models']}
                                            value={normalizedMassSmsDeviceModels}
                                            onChange={(value) => {
                                                setMassSmsDeviceModels(normalizeToArray(value));
                                                clearMassCommandState();
                                            }}
                                            options={availableDeviceModels}
                                            textField="name"
                                            valueField="id"
                                            menuPlacement="auto"
                                            loading={loadingEntity}
                                            placeholder={messages['send_commands_device_models']}
                                            isMulti
                                        />
                                    </div>
                                </Grid>
                            }
                            {
                                massSMS && (
                                    <>
                                        {massCommandGroups?.manufacturer?.cannedCommands?.length > 0 &&
                                            <Grid item xs={12}>
                                                <div style={{ marginTop: 10 }}>
                                                    <Select
                                                        id="manufacturer-command-list"
                                                        value={massCommandSelections.manufacturer || null}
                                                        valueField="id"
                                                        textField="label"
                                                        options={massCommandGroups.manufacturer.cannedCommands}
                                                        label={messages['send_commands_manufacturer_commands']}
                                                        placeholder={messages['send_commands_canned_commands']}
                                                        onChange={(value) => updateMassCommandSelection('manufacturer', value)}
                                                        isClearable
                                                    />
                                                </div>
                                            </Grid>
                                        }
                                        {massCommandGroups?.unitTypes?.map((item) => (
                                            <Grid item xs={12} key={`unitType-command-${item.id}`}>
                                                <div style={{ marginTop: 10 }}>
                                                    <Select
                                                        id={`unitType-command-list-${item.id}`}
                                                        value={massCommandSelections[`unitType-${item.id}`] || null}
                                                        valueField="id"
                                                        textField="label"
                                                        options={item.cannedCommands}
                                                        label={`${messages['send_commands_unit_type_commands']}: ${item.name}`}
                                                        placeholder={messages['send_commands_canned_commands']}
                                                        onChange={(value) => updateMassCommandSelection(`unitType-${item.id}`, value)}
                                                        isClearable
                                                    />
                                                </div>
                                            </Grid>
                                        ))}
                                        {massCommandGroups?.deviceModels?.map((item) => (
                                            <Grid item xs={12} key={`deviceModel-command-${item.id}`}>
                                                <div style={{ marginTop: 10 }}>
                                                    <Select
                                                        id={`deviceModel-command-list-${item.id}`}
                                                        value={massCommandSelections[`deviceModel-${item.id}`] || null}
                                                        valueField="id"
                                                        textField="label"
                                                        options={item.cannedCommands}
                                                        label={`${messages['send_commands_device_model_commands']}: ${item.name}`}
                                                        placeholder={messages['send_commands_canned_commands']}
                                                        onChange={(value) => updateMassCommandSelection(`deviceModel-${item.id}`, value)}
                                                        isClearable
                                                    />
                                                </div>
                                            </Grid>
                                        ))}
                                    </>
                                )
                            }
                            <Grid item xs={12}>
                                {
                                    !massSMS && sendCommandBy !== 'HTTP' &&
                                    <div style={{ display: 'grid' }}>
                                        <label className="MuiTypography-root MuiFormControlLabel-label MuiTypography-body1" style={{ marginRight: 5, marginBottom: 10 }}>{messages['devices_bulk_actions_open_command']}</label>
                                        <ToggleSwitch
                                            id="send-command-toogle-open-command"
                                            medium
                                            blueToggle
                                            optionLabels={[messages['devices_bulk_yes'], messages['devices_bulk_no']]}
                                            checked={openCommand}
                                            onChange={(checked) => {
                                                setOpenCommand(checked)
                                                if (!checked) setRawCommand("");
                                                if (checked) setCannedCommand(null);
                                            }}
                                        />
                                    </div>
                                }
                                <Form
                                    ref={commandRef}
                                    onValidSubmit={() => applyChanges()}
                                >
                                    {
                                        !openCommand && !massSMS &&
                                        <div style={{ marginTop: 10 }}>
                                            <Select
                                                id="send-command-list"
                                                value={cannedCommand}
                                                valueField="id"
                                                textField="label"
                                                options={commands || []}
                                                label=""
                                                placeholder={messages['send_commands_canned_commands']}
                                                onChange={(value) => {
                                                    setCannedCommand(value)
                                                }}
                                                required
                                                requiredMessage={messages['required_field']}
                                            />
                                        </div>
                                    }

                                    {
                                        !massSMS && sendCommandBy === 'HTTP' && isLocateCannedCommandSelected &&
                                        <div style={{ marginTop: 10, display: 'grid', gap: 10 }}>
                                            <Input
                                                type="number"
                                                label={messages['send_commands_canned_accuracy']}
                                                value={locateAccuracy}
                                                onChange={(value) => setLocateAccuracy(value)}
                                                onKeyDown={(e) => {
                                                    if (!/^[0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                onBeforeInput={(e) => {
                                                    if (e.data && !/^[0-9]+$/.test(e.data)) {
                                                        e.preventDefault();
                                                    }
                                                }}
                                                max={501}
                                                min={1}
                                                regex={"^[0-9]+$"}
                                                requiredMessage={messages["common_field_required"]}
                                                maxMessage={messages["send_commands_canned_accuracy_maximum_value_500"]}
                                                minMessage={messages["send_commands_canned_accuracy_minimum_value_1"]}
                                            />

                                            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                                                <ToggleSwitch
                                                    id="m2md-locate-skipwifi"
                                                    large
                                                    blueToggle
                                                    optionLabels={[messages['send_commands_canned_skip_wifi_on'], messages['send_commands_canned_skip_wifi_off']]}
                                                    checked={locateSkipWifi}
                                                    onChange={(checked) => setLocateSkipWifi(checked)}
                                                />
                                                <ToggleSwitch
                                                    id="m2md-locate-skipgps"
                                                    large
                                                    blueToggle
                                                    optionLabels={[messages['send_commands_canned_skip_gps_on'], messages['send_commands_canned_skip_gps_off']]}
                                                    checked={locateSkipGps}
                                                    onChange={(checked) => setLocateSkipGps(checked)}
                                                />
                                            </div>
                                        </div>
                                    }
                                    {
                                        (openCommand || massSMS) && sendCommandBy !== 'HTTP' &&
                                        <div style={{ marginTop: 10 }} className="raw-command-container send-command-phone">
                                            <Input
                                                label={!massSMS ? messages['devices_bulk_actions_raw_command'] : messages['send_commands_command_to_send']}
                                                value={rawCommand}
                                                type="textarea"
                                                style={{ width: '100%', height: 500 }}
                                                onChange={(value) => setRawCommand(value)}
                                                required
                                                requiredMessage={messages['required_field']}
                                            />
                                        </div>
                                    }
                                    {
                                        massSMS &&
                                        <div className="send-command-phone" style={{ marginTop: 10, height: 500 }} >
                                            <Input
                                                type="textarea"
                                                label={phoneNumberTypes.includes(type) ? "ICCID's" : messages['send_commands_phone_numbers']}
                                                value={phoneNumbers}
                                                onChange={(value) => setPhoneNumbers(value)}
                                                required
                                                requiredMessage={messages['required_field']}
                                                maxLength={5000}
                                                rows={13}
                                            />
                                        </div>
                                    }
                                </Form>
                                <div style={{ marginTop: 10 }}>
                                    {
                                        sentMessages.length > 0
                                            ?
                                            <Alert severity="info">
                                                <AlertTitle>Commands Sent</AlertTitle>
                                                {
                                                    sentMessages.map((item, index) => {
                                                        return (
                                                            <div style={{ display: 'grid' }} key={index}>
                                                                <span>{`- ${item}`}</span>
                                                            </div>
                                                        )
                                                    })
                                                }
                                            </Alert>
                                            : null
                                    }
                                </div>
                            </Grid>
                        </Grid>
                    </div>
                }
                {
                    step === 3 &&
                    <div style={{ padding: '0 10px', marginBottom: 25 }}>
                        {
                            devices.map((item, index) => {
                                return (
                                    <CustomCard
                                        key={index}
                                        onClick={() => {
                                            let currentDevices = devices;
                                            let selected = currentDevices.findIndex((device) => device.id === item.id);
                                            if (selected > -1) currentDevices.splice(selected, 1);
                                            dispatch(sendCommandSetDevices(currentDevices));
                                        }}
                                        hideButton={step === 3}
                                    >
                                        <div style={{ display: 'grid' }}>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_imei']}</label><span>{item?.imei}</span>
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_serial']}</label><span>{item?.esn}</span>
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_model']}</label><span>{item?.deviceModel?.name}</span>
                                            </div>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{messages['send_commands_status']}</label>
                                                <Success style={{ marginRight: 5, color: '#52C6AD' }} />
                                                <span>{messages['send_commands_complete']}</span>
                                            </div>
                                        </div>
                                    </CustomCard>
                                )
                            })
                        }
                        {
                            massSMS &&
                            phones.length > 0 &&
                            <div>
                                <Grid container spacing={2}>
                                    <Grid item xs={5} style={{ alignItems: 'center' }}>
                                        <label style={{ fontWeight: 700, marginRight: 5, marginTop: 10 }}>{messages['send_commands_sms_activity']}</label>
                                        <Tooltip title={messages["device_overview_refresh_cameras_device_config"]} >
                                            <a className="btn-link align-middle" onClick={() => {
                                                dispatch(sendCommandGetDetailsRequest({ type: phoneNumberTypes.includes(type) ? 'ICCID' : 'MSISDN', entities: phones }))
                                            }}>
                                                <Icon>
                                                    cached
                                                </Icon>
                                            </a>
                                        </Tooltip>
                                    </Grid>
                                    <Grid item xs={7}>
                                        <div className="d-flex" style={{ marginLeft: 15, marginTop: 5, justifyContent: 'flex-end' }}>
                                            <div
                                                className="text-bold"
                                                style={{ marginLeft: 20, cursor: "pointer" }}
                                                onClick={() => collapseAll(!expandedAll)}
                                            >
                                                <label>
                                                    {
                                                        !expandedAll &&
                                                        messages['vidfleet_configuration_expand_all']
                                                    }
                                                    {
                                                        expandedAll &&
                                                        messages['vidfleet_configuration_collapse_all']
                                                    }
                                                </label>
                                            </div>
                                            <div onClick={() => collapseAll(!expandedAll)}>
                                                <Icon style={{ padding: 2, cursor: "pointer" }}>
                                                    {!expandedAll ? "expand_more" : "expand_less"}
                                                </Icon>
                                            </div>
                                        </div>
                                    </Grid>
                                </Grid>

                                <div style={{ display: "-webkit-inline-box", width: "80%", justifyContent: "center", alignItems: "center" }}>


                                </div>
                            </div>
                        }
                        {
                            massSMS &&
                            smsDetails.map((item, index) => {
                                let inMsg = item?.in ? JSON.parse(item?.in) : null;
                                let outMsg = item?.out ? JSON.parse(item?.out) : null;
                                return (
                                    <CustomCard
                                        key={index}
                                        hideButton={step === 3}
                                        width={12}
                                    >
                                        <div style={{ display: 'grid' }}>
                                            <div style={{ width: "100%" }}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={11}>
                                                        <label style={{ fontWeight: 700, marginRight: 5 }}>{phoneNumberTypes.includes(type) ? item.iccid : item.msisdn}</label>
                                                    </Grid>
                                                    <Grid item xs={1}>
                                                        <div onClick={() => {
                                                            let items = [...smsDetails];
                                                            items[index].expanded = !items[index].expanded;
                                                            setSmsDetails(items);
                                                        }}>
                                                            <Icon style={{ padding: 2, cursor: "pointer" }}>
                                                                {!item?.expanded ? "expand_more" : "expand_less"}
                                                            </Icon>
                                                        </div>
                                                    </Grid>
                                                </Grid>
                                                {
                                                    (inMsg?.length === 1 || (inMsg?.length > 1 && !smsDetails[index].expanded)) &&
                                                    <Grid container spacing={1}>
                                                        <Grid item xs={2}>
                                                            <Chip
                                                                style={{
                                                                    marginRight: 5,
                                                                    cursor: 'pointer',
                                                                    backgroundColor: "#66BB6A",
                                                                    color: 'white'
                                                                }}
                                                                label={`${messages["send_commands_inbound"]} (${inMsg.length})`}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={2}><label style={{ fontWeight: 500, marginRight: 5 }}>{inMsg[0].createdOn.replace('T', ' ').replace('Z', '').split(' ')[1].split('.')[0]}</label></Grid>
                                                        <Grid item xs={8}>
                                                            <Tooltip title={messages["send_commands_click_to_copy"]}>
                                                                <label onClick={() => { copyToClipboard(inMsg[0].message, messages['common_copied']) }}> {inMsg[0].message}</label>
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                }
                                                {
                                                    inMsg?.length > 1 && smsDetails[index].expanded &&
                                                    <div>
                                                        <Chip
                                                            style={{
                                                                marginRight: 5,
                                                                cursor: 'pointer',
                                                                backgroundColor: "#66BB6A",
                                                                color: 'white'
                                                            }}
                                                            label={`${messages["send_commands_inbound"]} (${inMsg.length})`}
                                                        />
                                                        {
                                                            inMsg.map((itemOut, indexOut) => {
                                                                return (
                                                                    <Grid container spacing={2} style={{ marginTop: 10 }} >
                                                                        <Grid item xs={2}>

                                                                        </Grid>
                                                                        <Grid item xs={2}><label style={{ fontWeight: 500, marginRight: 5 }}>{itemOut.createdOn.replace('T', ' ').replace('Z', '').split(' ')[1].split('.')[0]}</label></Grid>
                                                                        <Grid item xs={8}>
                                                                            <Tooltip title={messages["send_commands_click_to_copy"]}>
                                                                                <label onClick={() => { copyToClipboard(itemOut.message, messages['common_copied']) }}> {itemOut.message}</label>
                                                                            </Tooltip>
                                                                        </Grid>
                                                                    </Grid>
                                                                )
                                                            })
                                                        }
                                                    </div>
                                                }
                                                {
                                                    outMsg &&
                                                    <Grid container spacing={2} style={{ marginTop: 10, display: 'flex', alignItems: 'center' }}>
                                                        <Grid item xs={2}>
                                                            {
                                                                outMsg?.errorCode && outMsg?.errorMessage ? (
                                                                    <Tooltip title={outMsg?.errorMessage}>
                                                                        <Chip
                                                                            style={{
                                                                                marginRight: 5,
                                                                                cursor: 'pointer',
                                                                                backgroundColor: "#ff0000",
                                                                                color: 'white',
                                                                                marginTop: -5
                                                                            }}
                                                                            label="Error"
                                                                        />
                                                                    </Tooltip>
                                                                ) : (
                                                                    <Chip
                                                                        style={{
                                                                            marginRight: 5,
                                                                            cursor: 'pointer',
                                                                            backgroundColor: "#66BB6A",
                                                                            color: 'white'
                                                                        }}
                                                                        label={`${messages["send_commands_outbound"]}`}
                                                                    />
                                                                )
                                                            }
                                                        </Grid>
                                                        <Grid item xs={2}><label style={{ fontWeight: 500, marginRight: 5 }}>{outMsg.createdOn.replace('T', ' ').replace('Z', '').split(' ')[1].split('.')[0]}</label></Grid>
                                                        <Grid item xs={8}>
                                                            <Tooltip title={messages["send_commands_click_to_copy"]}>
                                                                <label onClick={() => { copyToClipboard(outMsg.message, messages['common_copied']) }}> {outMsg.message}</label>
                                                            </Tooltip>
                                                        </Grid>
                                                    </Grid>
                                                }
                                            </div>
                                        </div>
                                    </CustomCard>
                                )
                            })
                        }
                        {
                            massSMS &&
                            phoneNumbersErrors.length > 0 &&
                            <div>
                                <label style={{ fontWeight: 700, marginRight: 5, marginTop: 10 }}>{messages['send_commands_errors']}</label>
                            </div>
                        }
                        {
                            massSMS &&
                            phoneNumbersErrors.map((item, index) => {
                                return (
                                    <CustomCard
                                        key={index}
                                        hideButton={step === 3}
                                    >
                                        <div style={{ display: 'grid' }}>
                                            <div>
                                                <label style={{ fontWeight: 700, marginRight: 5 }}>{item.phoneNumber}</label>
                                                <Error style={{ marginRight: 5, color: '#c94747' }} />
                                                <span>{messages[item.error]}</span>
                                            </div>
                                        </div>
                                    </CustomCard>
                                )
                            })
                        }
                    </div>
                }
            </DialogContent>
            <DialogActions>
                <Button
                    className={step === 3 ? "btn-dark-blue" : "btn-cancel"}
                    onClick={() => closeModal()}
                >
                    {step === 3 ? messages['close'] : messages['cancel']}
                </Button>
                {
                    step < 3 ?
                        <Button
                            disabled={(!massSMS && (devices.length === 0 || unitTypeIdValidation)) || (massSMS && (rawCommand === "" || phoneNumbers === "" || sendType === null)) || loading}
                            className="btn-dark-blue"
                            onClick={() => {
                                step === 2 || single || massSMS ? commandRef.current?.submit() : stepHandler();
                            }}
                        >
                            {step === 2 || single || massSMS ? messages['send_commands_send'] : messages['common_next']}
                        </Button>
                        :
                        <Button
                            disabled={unitTypeIdValidation}
                            className="btn-dark-blue"
                            onClick={() => {
                                setRawCommand("");
                                setCannedCommand(null);
                                setPhoneNumbersErrors([]);
                                setPhones([]);
                                setStep(2);
                                dispatch(clearRedux("SEND_COMMAND", { detailsResult: [] }))
                                setExpandedAll(false)
                            }}
                        >
                            {messages['send_commands_send_more_commands']}
                        </Button>
                }
            </DialogActions>
            <NotificationContainer />
        </BootstrapDialog>
    )
}

export default SendCommand;
